import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { TicketView, TicketCommentView } from '../../../services/TicketService';
import { PreventiveMaintenanceRequestView, CostRecordView, CustomApplicationRequestView, ReportingRequestView } from '../../../types/shared/accessRequestTypes';
import {
  createDefaultCpsiAccessRequest,
  createDefaultHstAccessRequest,
  createDefaultFacilityMaintenanceRequest,
  createDefaultPreventiveMaintenanceRequest,
  createDefaultCostRecord,
  createDefaultCustomApplicationRequest,
  createDefaultReportingRequest,
} from '../../../types/shared/requestDefaults';
import { createDefaultTicket, ticketApi } from '../../../services/TicketService';
import { toLocalDateString } from '../../../utils/DateConversions';

// API responses carry a full ISO dueDate; the Due Date field edits a plain YYYY-MM-DD string.
function normalizeTicketDates(ticket: TicketView): TicketView {
  const { dueDate } = ticket.timeTracking;
  if (!dueDate) return ticket;
  return { ...ticket, timeTracking: { ...ticket.timeTracking, dueDate: toLocalDateString(dueDate) } };
}

export interface TicketState {
  currentTicket: TicketView | null;
  /** The unmodified view last fetched or saved — the baseline for the next 3-way merge (spec 010). */
  baseTicket: TicketView | null;
  selectedTicketId: string | null;
  isEdited: boolean;
  editedFields: string[];
  ui: {
    showAddNoteDialog: boolean;
    showViewNoteDialog: boolean;
    selectedNote: TicketCommentView | null;
  };
}

const initialState: TicketState = {
  currentTicket: null,
  baseTicket: null,
  selectedTicketId: null,
  isEdited: false,
  editedFields: [],
  ui: {
    showAddNoteDialog: false,
    showViewNoteDialog: false,
    selectedNote: null,
  },
};

const addEditedField = (state: TicketState, fieldName: string) => {
  if (!state.editedFields.includes(fieldName)) {
    state.editedFields.push(fieldName);
  }
  state.isEdited = true;
};

/** Sync denormalized root-level fields from their nested value objects. */
const syncDenormalizedFields = (ticket: TicketView) => {
  ticket.title = ticket.identification.title;
  ticket.description = ticket.identification.description;
  ticket.category = ticket.identification.category;
  ticket.priority = ticket.priorityInfo.priority;
  ticket.urgency = ticket.priorityInfo.urgency;
  ticket.status = ticket.workflow.status;
  ticket.assignedTo = ticket.assignment.assignedTo;
  ticket.assignedToName = ticket.assignment.assignedToName;
  ticket.customerName = ticket.customerInfo.customerName;
};

const ticketSlice = createSlice({
  name: 'ticket',
  initialState,
  reducers: {
    updateTicket: (state, action: PayloadAction<Partial<TicketView>>) => {
      if (state.currentTicket) {
        state.currentTicket = { ...state.currentTicket, ...action.payload };
        Object.keys(action.payload).forEach(field => {
          if (!state.editedFields.includes(field)) {
            state.editedFields.push(field);
          }
        });
        state.isEdited = true;
        syncDenormalizedFields(state.currentTicket);
      }
    },
    
    initializeTicket: (state) => {
      state.currentTicket = createDefaultTicket();
      state.baseTicket = null;
      state.isEdited = false;
      state.editedFields = [];
    },
    
    clearTicket: (state) => {
      state.currentTicket = null;
      state.baseTicket = null;
      state.selectedTicketId = null;
      state.isEdited = false;
      state.editedFields = [];
    },
    
    setSelectedTicketId: (state, action: PayloadAction<string | null>) => {
      state.selectedTicketId = action.payload;
    },

    // ── Identification ─────────────────────────────────────────────────
    updateTicketIdentification: (state, action: PayloadAction<Partial<TicketView['identification']>>) => {
      if (state.currentTicket) {
        state.currentTicket.identification = { 
          ...state.currentTicket.identification, 
          ...action.payload 
        };
        syncDenormalizedFields(state.currentTicket);
        addEditedField(state, 'identification');
      }
    },

    // ── Priority ───────────────────────────────────────────────────────
    updateTicketPriorityInfo: (state, action: PayloadAction<Partial<TicketView['priorityInfo']>>) => {
      if (state.currentTicket) {
        state.currentTicket.priorityInfo = { 
          ...state.currentTicket.priorityInfo, 
          ...action.payload 
        };
        syncDenormalizedFields(state.currentTicket);
        addEditedField(state, 'priorityInfo');
      }
    },

    // ── Assignment ─────────────────────────────────────────────────────
    updateTicketAssignment: (state, action: PayloadAction<Partial<TicketView['assignment']>>) => {
      if (state.currentTicket) {
        state.currentTicket.assignment = { 
          ...state.currentTicket.assignment, 
          ...action.payload 
        };
        const { assignedTo, assignedTeam } = state.currentTicket.assignment;
        state.currentTicket.assignment.isAssigned = !!(assignedTo || assignedTeam);
        state.currentTicket.assignment.isUnassigned = !(assignedTo || assignedTeam);
        state.currentTicket.assignment.hasIndividualAssignee = !!assignedTo;
        state.currentTicket.assignment.hasTeamAssignment = !!assignedTeam;
        syncDenormalizedFields(state.currentTicket);
        addEditedField(state, 'assignment');
      }
    },

    // ── Workflow ────────────────────────────────────────────────────────
    updateTicketWorkflow: (state, action: PayloadAction<Partial<TicketView['workflow']>>) => {
      if (state.currentTicket) {
        state.currentTicket.workflow = { 
          ...state.currentTicket.workflow, 
          ...action.payload 
        };
        syncDenormalizedFields(state.currentTicket);
        state.currentTicket.timeTracking.updatedDate = new Date().toISOString();
        state.currentTicket.isResolved = 
          state.currentTicket.workflow.status === 'Resolved' || 
          state.currentTicket.workflow.status === 'Closed';
        if (action.payload.status === 'Resolved' && !state.currentTicket.timeTracking.resolvedDate) {
          state.currentTicket.timeTracking.resolvedDate = new Date().toISOString();
        }
        if (action.payload.status === 'Closed' && !state.currentTicket.timeTracking.closedDate) {
          state.currentTicket.timeTracking.closedDate = new Date().toISOString();
        }
        addEditedField(state, 'workflow');
      }
    },

    // ── Time Tracking ──────────────────────────────────────────────────
    updateTicketTimeTracking: (state, action: PayloadAction<Partial<TicketView['timeTracking']>>) => {
      if (state.currentTicket) {
        state.currentTicket.timeTracking = { 
          ...state.currentTicket.timeTracking, 
          ...action.payload 
        };
        addEditedField(state, 'timeTracking');
      }
    },

    // ── Customer Info ──────────────────────────────────────────────────
    updateTicketCustomerInfo: (state, action: PayloadAction<Partial<TicketView['customerInfo']>>) => {
      if (state.currentTicket) {
        state.currentTicket.customerInfo = { 
          ...state.currentTicket.customerInfo, 
          ...action.payload 
        };
        syncDenormalizedFields(state.currentTicket);
        addEditedField(state, 'customerInfo');
      }
    },

    // ── Resolution ─────────────────────────────────────────────────────
    updateTicketResolution: (state, action: PayloadAction<TicketView['resolution']>) => {
      if (state.currentTicket) {
        state.currentTicket.resolution = action.payload;
        addEditedField(state, 'resolution');
      }
    },
    clearTicketResolution: (state) => {
      if (state.currentTicket) {
        state.currentTicket.resolution = { description: '', category: undefined };
        addEditedField(state, 'resolution');
      }
    },
    
    // ── Comments ───────────────────────────────────────────────────────
    addComment: (state, action: PayloadAction<Omit<TicketCommentView, 'id'>>) => {
      if (state.currentTicket) {
        const newComment: TicketCommentView = {
          ...action.payload,
          id: `temp_${Date.now()}`,
        };
        if (!state.currentTicket.communication.comments) {
          state.currentTicket.communication.comments = [];
        }
        state.currentTicket.communication.comments.push(newComment);
        addEditedField(state, 'communication');
      }
    },
    updateComment: (state, action: PayloadAction<{ commentId: string; content: string }>) => {
      if (state.currentTicket) {
        const { commentId, content } = action.payload;
        const comments = state.currentTicket.communication.comments;
        const commentIndex = comments.findIndex((c: TicketCommentView) => c.id === commentId);
        if (commentIndex !== -1) {
          comments[commentIndex].comment = content;
          addEditedField(state, 'communication');
        }
      }
    },
    removeComment: (state, action: PayloadAction<string>) => {
      if (state.currentTicket) {
        const commentId = action.payload;
        state.currentTicket.communication.comments = state.currentTicket.communication.comments.filter(
          (c: TicketCommentView) => c.id !== commentId
        );
        addEditedField(state, 'communication');
      }
    },

    // ── Notes UI ───────────────────────────────────────────────────────
    openAddNoteDialog: (state) => { state.ui.showAddNoteDialog = true; },
    closeAddNoteDialog: (state) => { state.ui.showAddNoteDialog = false; },
    openViewNoteDialog: (state, action: PayloadAction<TicketCommentView>) => {
      state.ui.showViewNoteDialog = true;
      state.ui.selectedNote = action.payload;
    },
    closeViewNoteDialog: (state) => {
      state.ui.showViewNoteDialog = false;
      state.ui.selectedNote = null;
    },

    // ── General ────────────────────────────────────────────────────────
    resetTicket: (state) => {
      state.currentTicket = null;
      state.baseTicket = null;
      state.selectedTicketId = null;
      state.isEdited = false;
      state.editedFields = [];
    },
    markAsSaved: (state) => { state.isEdited = false; state.editedFields = []; },
    discardChanges: (state) => { state.isEdited = false; state.editedFields = []; },

    // ── CPSI Access Request ────────────────────────────────────────────
    updateCpsiAccessRequest: (state, action: PayloadAction<Partial<TicketView['cpsiAccessRequest']>>) => {
      if (state.currentTicket) {
        if (!state.currentTicket.cpsiAccessRequest) {
          state.currentTicket.cpsiAccessRequest = createDefaultCpsiAccessRequest();
        }
        state.currentTicket.cpsiAccessRequest = { 
          ...state.currentTicket.cpsiAccessRequest, 
          ...action.payload 
        };
        addEditedField(state, 'cpsiAccessRequest');
      }
    },
    clearCpsiAccessRequest: (state) => {
      if (state.currentTicket) {
        state.currentTicket.cpsiAccessRequest = undefined;
        addEditedField(state, 'cpsiAccessRequest');
      }
    },

    // ── HST Access Request ─────────────────────────────────────────────
    updateHstAccessRequest: (state, action: PayloadAction<Partial<TicketView['hstAccessRequest']>>) => {
      if (state.currentTicket) {
        if (!state.currentTicket.hstAccessRequest) {
          state.currentTicket.hstAccessRequest = createDefaultHstAccessRequest();
        }
        state.currentTicket.hstAccessRequest = { 
          ...state.currentTicket.hstAccessRequest, 
          ...action.payload 
        };
        addEditedField(state, 'hstAccessRequest');
      }
    },
    clearHstAccessRequest: (state) => {
      if (state.currentTicket) {
        state.currentTicket.hstAccessRequest = undefined;
        addEditedField(state, 'hstAccessRequest');
      }
    },

    // ── Facility Maintenance Request ───────────────────────────────────
    updateFacilityMaintenanceRequest: (state, action: PayloadAction<Partial<TicketView['facilityMaintenanceRequest']>>) => {
      if (state.currentTicket) {
        if (!state.currentTicket.facilityMaintenanceRequest) {
          state.currentTicket.facilityMaintenanceRequest = createDefaultFacilityMaintenanceRequest();
        }
        state.currentTicket.facilityMaintenanceRequest = { 
          ...state.currentTicket.facilityMaintenanceRequest, 
          ...action.payload 
        };
        addEditedField(state, 'facilityMaintenanceRequest');
      }
    },
    clearFacilityMaintenanceRequest: (state) => {
      if (state.currentTicket) {
        state.currentTicket.facilityMaintenanceRequest = undefined;
        addEditedField(state, 'facilityMaintenanceRequest');
      }
    },

    // ── Preventive Maintenance Request ─────────────────────────────────
    updatePreventiveMaintenanceRequest: (state, action: PayloadAction<Partial<PreventiveMaintenanceRequestView>>) => {
      if (state.currentTicket) {
        if (!state.currentTicket.preventiveMaintenanceRequest) {
          state.currentTicket.preventiveMaintenanceRequest = createDefaultPreventiveMaintenanceRequest();
        }
        state.currentTicket.preventiveMaintenanceRequest = {
          ...state.currentTicket.preventiveMaintenanceRequest,
          ...action.payload,
        };
        addEditedField(state, 'preventiveMaintenanceRequest');
      }
    },
    clearPreventiveMaintenanceRequest: (state) => {
      if (state.currentTicket) {
        state.currentTicket.preventiveMaintenanceRequest = undefined;
        addEditedField(state, 'preventiveMaintenanceRequest');
      }
    },

    // ── Custom Application Request ─────────────────────────────────────
    updateCustomApplicationRequest: (state, action: PayloadAction<Partial<CustomApplicationRequestView>>) => {
      if (state.currentTicket) {
        if (!state.currentTicket.customApplicationRequest) {
          state.currentTicket.customApplicationRequest = createDefaultCustomApplicationRequest();
        }
        state.currentTicket.customApplicationRequest = {
          ...state.currentTicket.customApplicationRequest,
          ...action.payload,
        };
        addEditedField(state, 'customApplicationRequest');
      }
    },
    clearCustomApplicationRequest: (state) => {
      if (state.currentTicket) {
        state.currentTicket.customApplicationRequest = undefined;
        addEditedField(state, 'customApplicationRequest');
      }
    },

    // ── Reporting Request ───────────────────────────────────────────
    updateReportingRequest: (state, action: PayloadAction<Partial<ReportingRequestView>>) => {
      if (state.currentTicket) {
        if (!state.currentTicket.reportingRequest) {
          state.currentTicket.reportingRequest = createDefaultReportingRequest();
        }
        state.currentTicket.reportingRequest = {
          ...state.currentTicket.reportingRequest,
          ...action.payload,
        };
        addEditedField(state, 'reportingRequest');
      }
    },
    clearReportingRequest: (state) => {
      if (state.currentTicket) {
        state.currentTicket.reportingRequest = undefined;
        addEditedField(state, 'reportingRequest');
      }
    },

    // ── Cost Record ────────────────────────────────────────────────────
    updateCostRecord: (state, action: PayloadAction<Partial<CostRecordView>>) => {
      if (state.currentTicket) {
        if (!state.currentTicket.costRecord) {
          state.currentTicket.costRecord = createDefaultCostRecord();
        }
        state.currentTicket.costRecord = {
          ...state.currentTicket.costRecord,
          ...action.payload,
        };
        const cr = state.currentTicket.costRecord;
        cr.totalCost = (cr.laborCost ?? 0) + (cr.partsCost ?? 0) + (cr.vendorCost ?? 0);
        addEditedField(state, 'costRecord');
      }
    },
    clearCostRecord: (state) => {
      if (state.currentTicket) {
        state.currentTicket.costRecord = undefined;
        addEditedField(state, 'costRecord');
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        ticketApi.endpoints.getTicketById.matchFulfilled,
        (state, action) => {
          const normalized = normalizeTicketDates(action.payload);
          state.currentTicket = normalized;
          state.baseTicket = normalized;
          state.selectedTicketId = action.payload.id;
          state.isEdited = false;
          state.editedFields = [];
        }
      )
      .addMatcher(
        ticketApi.endpoints.upsertTicket.matchFulfilled,
        (state, action) => {
          const normalized = normalizeTicketDates(action.payload);
          state.currentTicket = normalized;
          // A successful save — plain apply or auto-merge — is the new baseline for the next edit.
          state.baseTicket = normalized;
          if (action.payload.id) {
            state.selectedTicketId = action.payload.id;
          }
          state.isEdited = false;
          state.editedFields = [];
        }
      );
  },
});

export const {
  updateTicket,
  initializeTicket,
  clearTicket,
  setSelectedTicketId,
  updateTicketIdentification,
  updateTicketPriorityInfo,
  updateTicketAssignment,
  updateTicketWorkflow,
  updateTicketTimeTracking,
  updateTicketCustomerInfo,
  updateTicketResolution,
  clearTicketResolution,
  addComment,
  updateComment,
  removeComment,
  openAddNoteDialog,
  closeAddNoteDialog,
  openViewNoteDialog,
  closeViewNoteDialog,
  resetTicket,
  markAsSaved,
  discardChanges,
  updateCpsiAccessRequest,
  clearCpsiAccessRequest,
  updateHstAccessRequest,
  clearHstAccessRequest,
  updateFacilityMaintenanceRequest,
  clearFacilityMaintenanceRequest,
  updatePreventiveMaintenanceRequest,
  clearPreventiveMaintenanceRequest,
  updateCustomApplicationRequest,
  clearCustomApplicationRequest,
  updateReportingRequest,
  clearReportingRequest,
  updateCostRecord,
  clearCostRecord,
} = ticketSlice.actions;

export const ticketReducer = ticketSlice.reducer;
export default ticketReducer;
